import {
  CuriosityAutocompleteProps,
  CuriosityNamespaceType,
  CuriosityAutocomplete,
  CuriosityNamespace,
  CuriosityAutocompleteValueWithNull,
  isCuriosityTranslation,
  CuriosityFilterByType,
  useCuriosityApiRef,
} from '@procureai/curiosity-ui-mui';
import { forwardRef, ForwardedRef, useMemo, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { SupplierContactEditDialog } from '../SupplierContactEditDialog/SupplierContactEditDialog.tsx';
import { useOpenClose } from '~/hooks';

import { SupplierAddOption } from './components/SupplierAddOption/SupplierAddOption.tsx';
import { SupplierContactOption } from './components/SupplierContactOption/SupplierContactOption.tsx';
import {
  SupplierContactAutocompleteHit,
  SupplierContactEntity,
  SupplierContactHitId,
} from './SupplierContactAutocomplete.types.ts';
import { createHitFromEditData, createStaticHit } from './SupplierContactAutocomplete.util.ts';

const ns = CuriosityNamespaceType.SUPPLIER_CONTACT;

export type SupplierContactAutocompleteProps<
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  Ns extends CuriosityNamespace = CuriosityNamespace,
> = Omit<CuriosityAutocompleteProps<SupplierContactEntity, Multiple, DisableClearable, Ns>, 'ns'>;

const SupplierContactAutocompleteInner = <
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  Ns extends CuriosityNamespace = CuriosityNamespace,
>(
  props: SupplierContactAutocompleteProps<Multiple, DisableClearable, Ns>,
  ref: ForwardedRef<unknown>,
) => {
  const apiRef = useCuriosityApiRef();
  const { t } = useTranslation(undefined, { keyPrefix: 'SupplierContactAutocomplete' });
  const supplierId =
    props.filter?.by === CuriosityFilterByType.SUPPLIER_ID ? (props.filter?.value as string) : undefined;

  const skipOpenRef = useRef<boolean>(false);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | undefined>(undefined);
  const [dialogProps, dialogOpen] = useOpenClose();

  const optionsAfter = useMemo(() => {
    // the contact form only supports adding a new contacts to a supplier
    // -> if the supplierId is undefined, we don't know the supplier
    if (!supplierId) {
      return undefined;
    }
    return () => [createStaticHit(SupplierContactHitId.NEW, t('manual'))];
  }, [t, supplierId]);

  const handleOpen = useCallback(() => {
    if (skipOpenRef.current) {
      skipOpenRef.current = false;
      return;
    }
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <>
      <CuriosityAutocomplete<SupplierContactEntity, Multiple, DisableClearable>
        ref={ref}
        apiRef={apiRef}
        ns={ns}
        optionsAfter={optionsAfter}
        renderOption={(params, optionProp) => {
          const option = optionProp as SupplierContactAutocompleteHit;
          const k = isCuriosityTranslation(option) ? option.key : option;
          const OptionComponent = k === SupplierContactHitId.NEW ? SupplierAddOption : SupplierContactOption;
          return (
            <OptionComponent
              {...params}
              key={k}
              data={option}
              onSecondaryActionClick={() => {
                setEditId(option.key !== SupplierContactHitId.NEW ? option.key : undefined);
                dialogOpen();
                handleClose();
              }}
            />
          );
        }}
        {...props}
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        onChange={(val) => {
          if (props.onChange) {
            const nextArr: CuriosityAutocompleteValueWithNull<SupplierContactEntity> = props.multiple
              ? (val as any)
              : [val];
            const mapped = nextArr.map((changedValue) => {
              if (
                changedValue &&
                isCuriosityTranslation(changedValue) &&
                changedValue.id === SupplierContactHitId.NEW
              ) {
                return changedValue.key;
              }
              return changedValue;
            });
            props.onChange(props.multiple ? (mapped as any) : mapped[0]);
          }
        }}
      />
      {supplierId ? (
        <SupplierContactEditDialog
          key={editId}
          supplierId={supplierId}
          contactId={editId}
          onSaveSuccess={(data) => {
            const cacheData = createHitFromEditData(data);
            // optimistic update for curiosity cache
            apiRef.current.cacheUpdate(cacheData.key, cacheData);
            // prevent the autocomplete from opening when the user selects a new contact
            skipOpenRef.current = true;
            props.onChange?.(cacheData as any);
          }}
          {...dialogProps}
        />
      ) : null}
    </>
  );
};

export const SupplierContactAutocomplete = forwardRef(SupplierContactAutocompleteInner) as <
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  Ns extends CuriosityNamespace = CuriosityNamespace,
>(
  props: SupplierContactAutocompleteProps<Multiple, DisableClearable, Ns> & {
    ref?: ForwardedRef<unknown>;
  },
) => ReturnType<typeof SupplierContactAutocompleteInner>;
